import React from 'react';
import { cn } from '@/lib/utils';

interface CashpurrProps {
  mood?: 'happy' | 'critical' | 'thinking' | 'sleeping' | 'angry' | 'excited';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  animate?: boolean;
  className?: string;
}

export const Cashpurr: React.FC<CashpurrProps> = ({ 
  mood = 'happy', 
  size = 'md', 
  animate = true,
  className 
}) => {
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32'
  };

  // SVG icons for different moods - using same icon for all moods for now
  const moodIcons = {
    happy: '/cashpurr-icon.svg',
    critical: '/cashpurr-icon.svg',
    thinking: '/cashpurr-icon.svg',
    sleeping: '/cashpurr-icon.svg',
    angry: '/cashpurr-icon.svg',
    excited: '/cashpurr-icon.svg'
  };

  // CSS filters to represent different moods
  const moodFilters = {
    happy: 'brightness(1.1) saturate(1.2)',
    critical: 'brightness(0.9) saturate(1.1) contrast(1.1)',
    thinking: 'brightness(0.95) saturate(0.9)',
    sleeping: 'brightness(0.8) saturate(0.7) opacity(0.8)',
    angry: 'brightness(0.85) saturate(1.3) contrast(1.2) hue-rotate(10deg)',
    excited: 'brightness(1.2) saturate(1.4) contrast(1.1)'
  };

  // Fallback emoji for different moods
  const fallbackEmoji = {
    happy: '😸',
    critical: '🧐',
    thinking: '🤔',
    sleeping: '😴',
    angry: '😾',
    excited: '🤩'
  };

  const [imageError, setImageError] = React.useState(false);

  return (
    <div className={cn(
      'flex items-center justify-center rounded-full bg-gradient-to-br from-accent/20 to-primary/20 overflow-hidden',
      sizeClasses[size],
      animate && 'cashpurr-purr',
      className
    )}>
      {!imageError ? (
        <img 
          src={moodIcons[mood]} 
          alt="Cashpurr"
          className="w-full h-full object-contain"
          style={{ filter: moodFilters[mood] }}
          onError={() => setImageError(true)}
        />
      ) : (
        <span className="text-2xl text-primary">
          {fallbackEmoji[mood]}
        </span>
      )}
    </div>
  );
};

interface CashpurrMessageProps {
  message: string;
  mood?: CashpurrProps['mood'];
  showAvatar?: boolean;
  className?: string;
}

export const CashpurrMessage: React.FC<CashpurrMessageProps> = ({
  message,
  mood = 'critical',
  showAvatar = true,
  className
}) => {
  return (
    <div className={cn(
      'flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20',
      className
    )}>
      {showAvatar && <Cashpurr mood={mood} size="sm" />}
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground leading-relaxed">
          {message}
        </p>
      </div>
    </div>
  );
};

export default Cashpurr;